/**
 * Admin API for modeling plan options and their 3D mesh assets.
 *
 * Every mutating route answers with the refreshed cycle payload, so callers get
 * the whole modeling tab back from one request and never have to merge state by
 * hand. Mesh bytes are fetched separately through {@link fetchMeshArrayBuffer}.
 */
import {
  ApiError,
  apiRequest,
  fetchAuthenticatedBlob,
  fetchAuthenticatedResponse,
  type AuthenticatedFetchOptions,
} from './client';
import { normalizeTreatmentStage } from './patients';
import { validatePlanSchemeImage, validateTreatmentMesh } from '../utils/fileValidation';
import type {
  JawType,
  MeshRole,
  PlanAlignerPricingMode,
  TreatmentStage,
} from '../types/admin';

/**
 * Above this, the raw-STL fallback is refused and the admin is told the preview
 * is still generating. Parsing a 19 MB scan blocks the main thread for seconds
 * and the optimised GLB is usually only moments away.
 */
export const MAX_STL_FALLBACK_BYTES = 25 * 1024 * 1024;

/** Roles an admin can upload against a plan option (movement steps use `step`). */
export type PlanMeshRole = Extract<MeshRole, 'current' | 'result'>;

export type MeshVariant = 'preview' | 'source';

export type PlanSchemeSlot = 'attachments' | 'buttons' | 'elastics' | 'separation';

export type PlanAlignerItemPayload = {
  readonly id: string;
  readonly type: string;
  readonly kind?: 'REGULAR' | 'WITH_OVERLAYS' | 'SETUP' | 'RETENTION_CAP' | 'CUSTOM' | null;
  readonly jaw?: JawType | null;
  readonly quantity: number;
  readonly unit_price: string;
};

export type PlanClinicalElementPayload = {
  readonly id: string;
  readonly title: string;
  readonly description?: string | null;
  readonly paid: boolean;
  readonly price?: string | null;
};

export type PlanDiscountPayload = {
  readonly id: string;
  readonly name: string;
  readonly comment?: string | null;
  readonly amount: string;
};

export type PlanAttachmentsPayload = {
  readonly enabled: boolean;
  readonly type?: 'Composite' | 'Negative' | 'Other' | null;
  readonly quantity?: number | null;
  readonly comment?: string | null;
};

export type PlanButtonsPayload = {
  readonly enabled: boolean;
  readonly quantity?: number | null;
  readonly type?: string | null;
  readonly unit_price: string;
};

export type PlanElasticsPayload = {
  readonly enabled: boolean;
  readonly sets?: number | null;
  readonly type?: string | null;
  readonly unit_price: string;
};

export type PlanSeparationPayload = {
  readonly enabled: boolean;
  readonly comment?: string | null;
  readonly price: string;
};

export type PlanOptionPayload = {
  readonly title?: string | null;
  readonly is_main?: boolean | null;
  readonly sort_order?: number | null;
  readonly is_published?: boolean | null;
  readonly upper_aligners_count?: number | null;
  readonly lower_aligners_count?: number | null;
  readonly clinical_comment?: string | null;
  readonly estimated_stages?: number | null;
  readonly max_course_aligners?: number | null;
  readonly setup_aligners_count?: number | null;
  readonly days_per_aligner?: number | null;
  readonly duration_override_days?: number | null;
  readonly aligner_pricing_mode?: PlanAlignerPricingMode | null;
  readonly standard_unit_price?: string | null;
  readonly aligner_items?: readonly PlanAlignerItemPayload[] | null;
  readonly clinical_elements?: readonly PlanClinicalElementPayload[] | null;
  readonly discounts?: readonly PlanDiscountPayload[] | null;
  readonly deposit_amount?: string | null;
  readonly attachments?: PlanAttachmentsPayload | null;
  readonly buttons?: PlanButtonsPayload | null;
  readonly elastics?: PlanElasticsPayload | null;
  readonly separation?: PlanSeparationPayload | null;
  readonly key_difference?: string | null;
};

function cyclePath(patientId: string, treatmentId: string, cycleId: string): string {
  return `/admin/patients/${patientId}/treatments/${treatmentId}/cycles/${cycleId}`;
}

export type CycleRef = {
  readonly patientId: string;
  readonly treatmentId: string;
  readonly cycleId: string;
};

function planOptionsPath(cycle: CycleRef): string {
  return `${cyclePath(cycle.patientId, cycle.treatmentId, cycle.cycleId)}/plan-options`;
}

/** Rejects unusable files before a 19 MB upload leaves the browser. */
function assertUploadableMesh(file: File): ApiError | null {
  const error = validateTreatmentMesh(file);
  if (error === 'type') {
    return new ApiError('Unsupported model file. Please upload a binary or ASCII .stl file.', 415);
  }
  if (error === 'size') {
    return new ApiError('Model file exceeds the 100 MB size limit.', 413);
  }
  return null;
}

function meshFormData(file: File): FormData {
  const body = new FormData();
  body.append('file', file);
  return body;
}

function assertUploadableScheme(file: File): ApiError | null {
  const error = validatePlanSchemeImage(file);
  if (error === 'type') {
    return new ApiError('Unsupported scheme image. Please upload a JPEG, PNG, or WebP file.', 415);
  }
  if (error === 'size') {
    return new ApiError('Scheme image exceeds the 10 MB size limit.', 413);
  }
  return null;
}

export function createPlanOption(
  cycle: CycleRef,
  payload: PlanOptionPayload,
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(planOptionsPath(cycle), {
    method: 'POST',
    body: payload,
    token: accessToken,
  }).then(normalizeTreatmentStage);
}

export function updatePlanOption(
  cycle: CycleRef,
  optionId: string,
  payload: PlanOptionPayload,
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(`${planOptionsPath(cycle)}/${optionId}`, {
    method: 'PATCH',
    body: payload,
    token: accessToken,
  }).then(normalizeTreatmentStage);
}

export function deletePlanOption(
  cycle: CycleRef,
  optionId: string,
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(`${planOptionsPath(cycle)}/${optionId}`, {
    method: 'DELETE',
    token: accessToken,
  }).then(normalizeTreatmentStage);
}

export function publishPlanOption(
  cycle: CycleRef,
  optionId: string,
  isPublished: boolean,
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(`${planOptionsPath(cycle)}/${optionId}/publish`, {
    method: 'POST',
    body: { is_published: isPublished },
    token: accessToken,
  }).then(normalizeTreatmentStage);
}

/** `null` clears the selection and the cycle's `plan_approved_at`. */
export function setPlanSelection(
  cycle: CycleRef,
  planOptionId: string | null,
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(
    `${cyclePath(cycle.patientId, cycle.treatmentId, cycle.cycleId)}/plan-selection`,
    {
      method: 'PUT',
      body: { plan_option_id: planOptionId },
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function uploadPlanOptionMesh(
  cycle: CycleRef,
  optionId: string,
  role: PlanMeshRole,
  jaw: JawType,
  file: File,
  accessToken: string,
): Promise<TreatmentStage> {
  const rejection = assertUploadableMesh(file);
  if (rejection) return Promise.reject(rejection);

  return apiRequest<TreatmentStage>(
    `${planOptionsPath(cycle)}/${optionId}/meshes/${role}/${jaw}`,
    {
      method: 'PUT',
      body: meshFormData(file),
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function deletePlanOptionMesh(
  cycle: CycleRef,
  optionId: string,
  role: PlanMeshRole,
  jaw: JawType,
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(
    `${planOptionsPath(cycle)}/${optionId}/meshes/${role}/${jaw}`,
    {
      method: 'DELETE',
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function uploadPlanScheme(
  cycle: CycleRef,
  optionId: string,
  slot: PlanSchemeSlot,
  file: File,
  accessToken: string,
): Promise<TreatmentStage> {
  const rejection = assertUploadableScheme(file);
  if (rejection) return Promise.reject(rejection);

  return apiRequest<TreatmentStage>(
    `${planOptionsPath(cycle)}/${optionId}/schemes/${slot}`,
    {
      method: 'PUT',
      body: meshFormData(file),
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function appendPlanScheme(
  cycle: CycleRef,
  optionId: string,
  slot: PlanSchemeSlot,
  file: File,
  accessToken: string,
): Promise<TreatmentStage> {
  const rejection = assertUploadableScheme(file);
  if (rejection) return Promise.reject(rejection);

  return apiRequest<TreatmentStage>(
    `${planOptionsPath(cycle)}/${optionId}/schemes/${slot}`,
    {
      method: 'POST',
      body: meshFormData(file),
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function deletePlanScheme(
  cycle: CycleRef,
  optionId: string,
  slot: PlanSchemeSlot,
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(
    `${planOptionsPath(cycle)}/${optionId}/schemes/${slot}`,
    {
      method: 'DELETE',
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function uploadClinicalElementScheme(
  cycle: CycleRef,
  optionId: string,
  elementId: string,
  file: File,
  accessToken: string,
): Promise<TreatmentStage> {
  const rejection = assertUploadableScheme(file);
  if (rejection) return Promise.reject(rejection);

  return apiRequest<TreatmentStage>(
    `${planOptionsPath(cycle)}/${optionId}/clinical-elements/${elementId}/scheme`,
    {
      method: 'PUT',
      body: meshFormData(file),
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function appendClinicalElementScheme(
  cycle: CycleRef,
  optionId: string,
  elementId: string,
  file: File,
  accessToken: string,
): Promise<TreatmentStage> {
  const rejection = assertUploadableScheme(file);
  if (rejection) return Promise.reject(rejection);

  return apiRequest<TreatmentStage>(
    `${planOptionsPath(cycle)}/${optionId}/clinical-elements/${elementId}/schemes`,
    {
      method: 'POST',
      body: meshFormData(file),
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function deleteClinicalElementScheme(
  cycle: CycleRef,
  optionId: string,
  elementId: string,
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(
    `${planOptionsPath(cycle)}/${optionId}/clinical-elements/${elementId}/scheme`,
    {
      method: 'DELETE',
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function deletePlanSchemeAsset(
  cycle: CycleRef,
  optionId: string,
  schemeId: string,
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(
    `${planOptionsPath(cycle)}/${optionId}/scheme-assets/${schemeId}`,
    {
      method: 'DELETE',
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function planSchemeContentPath(
  cycle: CycleRef,
  optionId: string,
  schemeId: string,
): string {
  return `${planOptionsPath(cycle)}/${optionId}/schemes/${schemeId}/content`;
}

export function fetchPlanSchemeBlob(
  cycle: CycleRef,
  optionId: string,
  schemeId: string,
  accessToken: string,
  options: AuthenticatedFetchOptions = {},
): Promise<Blob> {
  return fetchAuthenticatedBlob(
    planSchemeContentPath(cycle, optionId, schemeId),
    accessToken,
    options,
  );
}

export function uploadMovementStepMesh(
  cycle: CycleRef,
  stepIndex: number,
  jaw: JawType,
  file: File,
  accessToken: string,
): Promise<TreatmentStage> {
  const rejection = assertUploadableMesh(file);
  if (rejection) return Promise.reject(rejection);

  return apiRequest<TreatmentStage>(
    `${cyclePath(cycle.patientId, cycle.treatmentId, cycle.cycleId)}/movement-steps/${stepIndex}/${jaw}`,
    {
      method: 'PUT',
      body: meshFormData(file),
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function deleteMovementStepMesh(
  cycle: CycleRef,
  stepIndex: number,
  jaw: JawType,
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(
    `${cyclePath(cycle.patientId, cycle.treatmentId, cycle.cycleId)}/movement-steps/${stepIndex}/${jaw}`,
    {
      method: 'DELETE',
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

/**
 * Ask the server to regenerate the GLB preview of a failed mesh.
 *
 * The source STL is already stored, so this re-runs the conversion rather than
 * asking the admin to re-upload 19 MB.
 */
export function reprocessMesh(
  cycle: CycleRef,
  meshId: string,
  accessToken: string,
): Promise<TreatmentStage> {
  return apiRequest<TreatmentStage>(
    `${cyclePath(cycle.patientId, cycle.treatmentId, cycle.cycleId)}/meshes/${meshId}/reprocess`,
    {
      method: 'POST',
      token: accessToken,
    },
  ).then(normalizeTreatmentStage);
}

export function meshContentPath(cycle: CycleRef, meshId: string, variant: MeshVariant): string {
  const base = `${cyclePath(cycle.patientId, cycle.treatmentId, cycle.cycleId)}/meshes/${meshId}/content`;
  return `${base}?variant=${variant}`;
}

/**
 * Download mesh bytes for the 3D viewer.
 *
 * Mesh ids are immutable server-side (a replacement gets a new id), so the
 * result is safe to cache for the lifetime of the session. Pass `signal` — a
 * closed modal must not keep pulling megabytes.
 */
export function fetchMeshArrayBuffer(
  cycle: CycleRef,
  meshId: string,
  variant: MeshVariant,
  accessToken: string,
  options: AuthenticatedFetchOptions = {},
): Promise<ArrayBuffer> {
  return fetchAuthenticatedResponse(
    meshContentPath(cycle, meshId, variant),
    accessToken,
    options,
  ).then((response) => response.arrayBuffer());
}
